import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, Volume2, VolumeX } from "lucide-react";
import { LangProvider, useLang } from "@/i18n/LangContext";
import { Hero } from "@/components/Hero";
import { OverviewGrid } from "@/components/OverviewGrid";
import { Timeline } from "@/components/Timeline";
import { MapSection } from "@/components/MapSection";
import { TheoryMap } from "@/components/TheoryMap";
import { TocSidebar, TocMobile } from "@/components/Toc";
import { Chapter, PartHeading } from "@/components/Chapter";
import { Bibliography, Footer, AboutSection } from "@/components/Sections";
import { QuizPage } from "@/components/QuizPage";
import { EvidencePage } from "@/components/EvidencePage";
import { CriteriaBoardPage } from "@/components/CriteriaBoardPage";
import { ProgramPage } from "@/components/ProgramPage";
import { PublicationsPage } from "@/components/PublicationsPage";

// Μουσική στην αρχική σελίδα (εισαγωγή + στατική δίστηλη οθόνη)· ενεργή από προεπιλογή.
// Οι browsers απαγορεύουν αυτόματο ήχο πριν από επαφή του χρήστη — γι' αυτό η αναπαραγωγή
// ξεκινά με την πρώτη κίνηση (κλικ/κύλιση/πλήκτρο/άγγιγμα). Αν ο χρήστης πατήσει σίγαση,
// σεβόμαστε την επιλογή του και δεν την ανατρέπουμε με gestures.
const GlobalAudio = () => {
  const { ui } = useLang();
  const { pathname } = useLocation();
  const [muted, setMuted] = useState(false);
  const manualMute = useRef(false);
  const audioRef = useRef(null);
  // Η μουσική συνεχίζεται σε όλες τις σελίδες εκτός του παιδικού παιχνιδιού
  const live = pathname !== "/paidia";

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!live) {
      a.pause();
      return;
    }
    // Η σιωπηλή αναπαραγωγή επιτρέπεται από τους browsers: το κομμάτι ξεκινά και
    // φορτώνει (buffer) από το άνοιγμα της σελίδας, ώστε στην πρώτη αφή να ακουστεί αμέσως
    a.muted = true;
    a.play().then(() => setMuted(true)).catch(() => setMuted(true));
    const unmute = () => {
      if (manualMute.current) return;
      a.muted = false;
      setMuted(false);
      a.play().catch(() => {});
    };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, unmute, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, unmute));
  }, [live]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    manualMute.current = a.muted;
    setMuted(a.muted);
    if (!a.muted) a.play().catch(() => {});
  };
  return (
    <>
      <audio ref={audioRef} src="/media/hero-music.mp3" loop preload="auto" />
      {live && (
        <button
          type="button"
          onClick={toggle}
          data-testid="hero-video-sound"
          aria-label={muted ? ui.heroSoundOnLabel : ui.heroSoundOffLabel}
          title={muted ? ui.heroSoundOnLabel : ui.heroSoundOffLabel}
          className="fixed right-4 top-1/2 z-50 -translate-y-1/2 rounded-full border border-paper/40 bg-anthra/55 p-2.5 text-paper backdrop-blur-md transition-transform duration-200 hover:scale-110"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}
    </>
  );
};

// Το inline script του index.html βάζει τον χάρτη ως φόντο του body για άμεση πρώτη εμφάνιση.
// Η πλοήγηση είναι SPA (χωρίς reload), οπότε το φόντο θα έμενε και στις εσωτερικές σελίδες —
// το αφαιρούμε μόλις αλλάξει η διαδρομή.
const BootBackground = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/") document.body.classList.remove("boot-home");
  }, [pathname]);
  return null;
};

// Κάθε νέα σελίδα ανοίγει πάντα από την κορυφή (η SPA πλοήγηση κρατά αλλιώς τη θέση κύλισης)
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LangToggle = () => {
  const { lang, setLang } = useLang();
  const { pathname } = useLocation();
  // Ο επιλογέας γλώσσας εμφανίζεται μόνο στην αρχική σελίδα
  if (pathname !== "/") return null;
  return (
    <div data-testid="lang-toggle" className="fixed right-4 top-4 z-50 flex overflow-hidden rounded-full border border-line bg-paper/95 shadow-md backdrop-blur">
      {['el', 'en'].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors duration-150 ${
            lang === l ? 'bg-aegean text-paper' : 'text-foreground/60 hover:text-aegean'
          }`}
        >
          {l === 'el' ? 'ΕΛ' : 'EN'}
        </button>
      ))}
    </div>
  );
};

// Κεφαλίδα αυτοτελών σελίδων — ενιαίο αισθητικό στοιχείο (ανθρακί ζώνη όπως το hero)
const SubHeader = () => {
  const { ui } = useLang();
  return (
    <div data-testid="sub-header" className="border-b border-line bg-paper px-6 py-4 md:border-b-2 md:border-terra/70 md:bg-anthra">
      <div className="mx-auto flex max-w-6xl items-center gap-3">
        <Link
          to="/"
          data-testid="back-home"
          aria-label={ui.backHome}
          title={ui.backHome}
          className="shrink-0 rounded-full border border-ink/30 p-2.5 text-ink/70 transition-colors duration-200 hover:border-terra hover:text-terra md:border-paper/40 md:text-paper/80"
        >
          <HomeIcon className="h-4 w-4 md:h-4 md:w-4" />
        </Link>
        <p className="font-serif text-xl text-ink md:text-xl md:text-paper">{ui.heroTitle}</p>
      </div>
    </div>
  );
};

const ReadingTrack = () => {
  const { d, ui } = useLang();
  return (
    <div className="mx-auto max-w-6xl px-6 pb-16 pt-10 lg:grid lg:grid-cols-12 lg:gap-12 lg:py-24">
      <aside className="hidden lg:col-span-3 lg:block">
        <div className="sticky top-10 max-h-[85vh] overflow-y-auto pr-4">
          <TocSidebar />
        </div>
      </aside>
      <main className="lg:col-span-9">
        <TocMobile />
        <div data-testid="analysis-title" className="border-b border-line pb-6">
          <p className="eyebrow">{ui.analysisEyebrow}</p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl font-semibold tracking-tight text-ink">
            {ui.heroTitle}
          </h2>
          <p data-testid="analysis-abstract" className="mt-6 max-w-3xl text-base leading-relaxed text-foreground/75 md:text-lg">
            {ui.analysisAbstract}
          </p>
        </div>
        <Chapter chapter={d.PROLOGUE} compact />
        <PartHeading part={d.PART1} />
        {d.PART1.chapters.map((c) => <Chapter key={c.id} chapter={c} />)}
        <PartHeading part={d.PART2} />
        {d.PART2.chapters.map((c) => <Chapter key={c.id} chapter={c} />)}
        <PartHeading part={d.PART3} />
        {d.PART3.chapters.map((c) => <Chapter key={c.id} chapter={c} />)}
        <PartHeading part={d.PART4} />
        {d.PART4.chapters.map((c) => <Chapter key={c.id} chapter={c} />)}
        <div className="mt-24 border-t border-line pt-16">
          <Chapter chapter={d.EPILOGUE} />
        </div>
        <Bibliography />
      </main>
    </div>
  );
};

// Αρχική: μόνο το πάνω διπλό πλαίσιο (hero)
const Home = () => (
  <div data-testid="home-page">
    <LangToggle />
    <Hero />
  </div>
);

const OverviewPage = () => (
  <div data-testid="overview-page">
    <LangToggle />
    <SubHeader />
    <OverviewGrid />
    <Timeline />
    <MapSection />
    <TheoryMap />
    <Footer />
  </div>
);

const AnalysisPage = () => (
  <div data-testid="analysis-page">
    <LangToggle />
    <SubHeader />
    <ReadingTrack />
    <Footer />
  </div>
);

const QuizRoute = () => (
  <div data-testid="quiz-page">
    <LangToggle />
    <SubHeader />
    <QuizPage />
    <Footer />
  </div>
);

const BoardRoute = () => (
  <div data-testid="board-page">
    <LangToggle />
    <SubHeader />
    <CriteriaBoardPage />
    <Footer />
  </div>
);

const EvidenceRoute = () => (
  <div data-testid="evidence-page">
    <LangToggle />
    <SubHeader />
    <EvidencePage />
    <Footer />
  </div>
);

const PubsRoute = () => (
  <div data-testid="pubs-page">
    <LangToggle />
    <SubHeader />
    <PublicationsPage />
    <Footer />
  </div>
);

const ProgramRoute = () => (
  <div data-testid="program-page">
    <LangToggle />
    <SubHeader />
    <ProgramPage />
    <Footer />
  </div>
);

const AboutRoute = () => (
  <div data-testid="about-page">
    <LangToggle />
    <SubHeader />
    <AboutSection />
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <LangProvider>
        <BrowserRouter>
          <BootBackground />
          <ScrollToTop />
          <GlobalAudio />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/synopsi" element={<OverviewPage />} />
            <Route path="/analysi" element={<AnalysisPage />} />
            <Route path="/test" element={<QuizRoute />} />
            <Route path="/axiologisi" element={<BoardRoute />} />
            <Route path="/tekmiiria" element={<EvidenceRoute />} />
            <Route path="/dimosieuseis" element={<PubsRoute />} />
            <Route path="/programma" element={<ProgramRoute />} />
            <Route path="/sxetika" element={<AboutRoute />} />
          </Routes>
        </BrowserRouter>
      </LangProvider>
    </div>
  );
}

export default App;
